/* eslint-disable camelcase */
import '@sass/lastminute/lastminute.scss';
import '@module/LinkBox/LinkBox.js';
import { bindIntersectionObserver, documentReady, fadeIn, fadeOut, isMobileView, isVisible, lLink, logEvent, promoteFeature, qs, qsa, setUrlParameter, storedItemRead, urlParameterGet } from '@utils/toolbox';
import { eventCategories, events } from '@js/utils/event';
import { ConnectApi } from '@js/plugins/connectApi';
import { DisplayDirector } from '@js/components/displayDirector';
import LastMinuteSelect from '@/src/vue/components/LastMinute/LastMinuteSelect.vue';
import { createVueApp } from '@/src/vue/utils/createVueApp';
import { l } from '@utils/toolbox';
import { resultsRender } from '@js/components/resultsRender';

const lastminute = {
	elems: {},
	request: false,
	scrollLastPosition: 0,
	scrollPosition: 0,
	scrollTimer: 0,

	/**
	 * @type {DisplayDirector|null}
	 */
	displayDirector: null,

	initIntersectionObserver () {
		bindIntersectionObserver(
			qs('[data-intersection-observer]'),
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						this.infiniteScroll();
					}
				});
			},
			{ root: null, rootMargin: '30%', threshold: 0.1 }
		);
	},

	init () {
		this.elems.ListContainer = qs('#lastminute-list-container');
		this.elems.ListLoader = qs('#lastminute-list-loader');
		this.elems.ListHeader = qs('#list-view-header');
		this.elems.ListView = qs('#lastminute-list-view');

		// Scroll to list on load if requested
		if (typeof js_params.lastminuteScrollToList !== 'undefined') {
			window.scrollTo({ top: this.elems.ListView.offsetTop });
		}

		this.initSearchParameters();

		this.initAdults();

		this.initContentSelector();

		this.initSorting();

		this.initIntersectionObserver();

		resultsRender.init();

		this.displayDirector = new DisplayDirector();

		if (this.selected.content === 'all' && storedItemRead('promote-charter') === null && js_params.isPackageDealsEnabled) {
			if (isMobileView()) {
				promoteFeature(qs('.chipMenu--changeOffer .chipMenu--title'), 'charter', l('lastminute.promote.charter'), 'top');
			} else {
				promoteFeature(qs('[data-offer-status="packages"]'), 'charter', l('lastminute.promote.charter'), 'down');
			}
		}

		// Click events
		document.body.addEventListener('click', async (event) => {
			if (event.target.closest('[data-action=scroll-up]:not(.disabled)')) {
				window.scrollTo(0, 0);
			}

			if (event.target.closest('.resultCard__ota--flight')) {
				logEvent(events.clickoutResultList, eventCategories.clickout);

				return;
			}

			if (event.target.closest('.resultCard__ota--package')) {
				logEvent(events.clickoutPackageList, eventCategories.clickout);

				return;
			}

			if (event.target.closest('.modal--resultModal .otaLink')) {
				if (event.target.closest('.modal--resultModalPackage')) {
					logEvent(events.clickoutPackageViewMore, eventCategories.clickout);
				} else {
					logEvent(events.clickoutResultViewMore, eventCategories.clickout);
				}

				return;
			}

			if (event.target.closest('.resultCard.resultCard--lastminute-package')) {
				// Show loader
				fadeIn(this.elems.ListLoader, 200);
				const element = event.target.closest('.resultCard.resultCard--lastminute-package');
				event.stopPropagation();
				const modal = await resultsRender.buildPackageModal(element.dataset.responseId, 'lastminute');
				await fadeOut(this.elems.ListLoader, 0);
				modal.open();

				logEvent(events.viewmorePackage, eventCategories.viewmore);

				return;
			}

			if (event.target.closest('.resultCard.resultCard--lastminute')) {
				// Show the loader if the network request takes more than 300ms
				const loaderTimeout = setTimeout(() => {
					fadeIn(this.elems.ListLoader, 200);
				}, 300);

				const element = event.target.closest('.resultCard.resultCard--lastminute');

				const orgIndex = element.dataset.orgIndex;

				// Extract the response id from the url parameter
				const responseId = element.dataset.responseId;

				try {
					// Get the flight data from the backend
					const response = await ConnectApi('Lastminute', 'getFlightByHash', [responseId, orgIndex]);

					// If the request finishes before 300ms, clear the timeout and hide the loader
					clearTimeout(loaderTimeout);
					fadeOut(this.elems.ListLoader, 180);

					// Render the modal
					resultsRender.setData(response);
					const flight = resultsRender.response.flights[0];
					resultsRender
						.renderModal(responseId, flight, 0, {}, { className: 'modal--resultModal' }, {}, 'lastminute')
						.open();

					logEvent(events.viewmoreResult, eventCategories.viewmore);
				} catch (error) {
					// Handle any errors
					clearTimeout(loaderTimeout);
					fadeOut(this.elems.ListLoader, 180);
					console.error('Error fetching flight data:', error);
				}

				return;
			}

			// Clicking on a sorting column resets the list and calls to update url and list
			if (event.target.closest('[data-sorting]')) {
				event.preventDefault();

				this.selected.sort = event.target.closest('[data-sorting]')?.dataset?.sorting;
			}

			if (event.target.closest('[data-offer-status]')) {
				event.preventDefault();

				this.selected.content = event.target.closest('[data-offer-status]')?.dataset?.offerStatus;
			}
		});

		document.addEventListener('scroll', (event) => {
			if (window.scrollY <= 300) {
				qs('[data-action=scroll-up]').classList.add('disabled');
			} else {
				qs('[data-action=scroll-up]').classList.remove('disabled');
			}
		});
	},

	initSearchParameters () {
		const { lastminuteParams } = js_params;

		this.selected = reactive({
			searchParamsFromIata: [],
			searchParamsToIata: [],
			searchParamsLeaveDate: [],
			searchParamsTripLength: [],
			searchParamsAdults: lastminuteParams?.adults ?? ['1'],
			content: urlParameterGet('content') || 'all',
			sort: urlParameterGet('sort') || 'discount',
			page: 0
		});

		// The input for the vue component varies wildly. The config is used to determine how to normalize the input.
		// Each component is bound to a different property in the "selected" object.
		createVueApp(LastMinuteSelect, {
			config: 'searchParamsFromIata',
			initialValue: lastminuteParams,
			items: js_params.searchParamsFromIata,
			selected: this.selected.searchParamsFromIata
		}).mount('#multi-select-from');

		this.toIataProps = reactive({
			config: 'searchParamsToIata',
			initialValue: lastminuteParams,
			items: js_params.searchParamsToIata,
			selected: this.selected.searchParamsToIata,
			searchable: true
		});

		createVueApp(LastMinuteSelect, this.toIataProps).mount('#multi-select-to');

		createVueApp(LastMinuteSelect, {
			config: 'searchParamsLeaveDate',
			initialValue: lastminuteParams,
			items: js_params.searchParamsLeaveDate,
			selected: this.selected.searchParamsLeaveDate
		}).mount('#multi-select-leave');

		createVueApp(LastMinuteSelect, {
			config: 'searchParamsTripLength',
			initialValue: lastminuteParams,
			items: js_params.searchParamsTripLength,
			selected: this.selected.searchParamsTripLength
		}).mount('#multi-select-triplength');

		// Watch for changes in search parameters and trigger new results
		watch(
			() => [
				this.selected.searchParamsFromIata,
				this.selected.searchParamsToIata,
				this.selected.searchParamsLeaveDate,
				this.selected.searchParamsTripLength,
				this.selected.searchParamsAdults,
				this.selected.content
			],
			() => {
				this.resetPage();
				this.updateUrlAndGetResults();
			},
			{ deep: true }
		);
	},

	initAdults () {
		const addInputElement = qs('#multi-select-adults [data-action=add-passenger]');
		const removeInputElement = qs('#multi-select-adults [data-action=remove-passenger]');

		addInputElement.addEventListener('click', () => {
			if (this.selected.searchParamsAdults[0] === '1') {
				this.selected.searchParamsAdults[0] = '2';
			}

			render();
		});

		removeInputElement.addEventListener('click', () => {
			if (this.selected.searchParamsAdults[0] === '2') {
				this.selected.searchParamsAdults[0] = '1';
			}

			render();
		});

		const render = () => {
			const value = this.selected.searchParamsAdults[0];
			qs('#multi-select-adults .abPassengers__count').innerHTML = value;
			qs('.abPassengers__btn--disabled').classList.remove('abPassengers__btn--disabled');

			if (value === '2') {
				addInputElement.classList.add('abPassengers__btn--disabled');
			} else {
				removeInputElement.classList.add('abPassengers__btn--disabled');
			}
		};
		render();
	},

	initContentSelector () {
		// Set active tab based on selected content
		const mobileOfferBtn = qs('.chipMenu--changeOffer');
		const mobileOfferTitle = qs('.chipMenu--changeOffer .chipMenu--title .chip__text');
		const mobileSortTitle = qs('.chipMenu--changeSort .chipMenu--title .chip__text');

		const render = () => {
			// Mobile chip

			if (mobileOfferBtn) {
				mobileOfferBtn.dataset.offerActive = this.selected.content;

				switch (this.selected.content) {
					case 'packages':
						mobileOfferTitle.innerHTML = l('lastminute.navigation.changeOfferPackageDealsDescriptive');
						break;
					case 'all':
						mobileOfferTitle.innerHTML = l('lastminute.navigation.changeOfferAllDescriptive');
						break;
					case 'flights':
						mobileOfferTitle.innerHTML = l('lastminute.navigation.changeOfferFlightsDescriptive');
						break;
				}
			}

			if (this.selected.sort === 'best') {
				mobileSortTitle.dataset.currentSorting = l('lastminutePage.listFilters.best');
			} else if (this.selected.sort === 'discount') {
				mobileSortTitle.dataset.currentSorting = l('showresult.sort.priceandtime');
			}

			qsa('[data-offer-status]').forEach((filterStatusElement) => {
				filterStatusElement.classList.remove('active');

				if (filterStatusElement.dataset.offerStatus === this.selected.content) {
					filterStatusElement.classList.add('active');
				}
			});
		};

		// Keep DOM in sync with model
		watch([() => this.selected.content, () => this.selected.sort], render, { immediate: true });
	},

	initSorting () {
		const render = () => {
			qsa('[data-sorting]').forEach((sortElement) => {
				sortElement.classList.remove('active');

				if (sortElement.dataset.sorting === this.selected.sort) {
					sortElement.classList.add('active');
				}
			});
		};

		watch(() => this.selected.sort, () => {
			console.log('sorting changed');
			this.resetPage();
			this.updateUrlAndGetResults();
			render();
		});
	},

	updateUrlAndGetResults () {
		const params = this.buildApiParams();
		const url = this.buildUrl(params);

		window.history.replaceState({}, '', url);

		this.getResults(params);

		if (js_params.device === 'MOBILE') {
			window.scrollTo({ top: this.elems.ListView.offsetTop });
		}
	},

	infiniteScroll () {
		if (this.elems.ListContainer.querySelectorAll('.lastminute-list-row-noresults').length > 0) {
			return false;
		}

		// Increment page and search again
		this.selected.page += 1;
		this.getResults(this.buildApiParams());
	},

	resetPage () {
		this.selected.page = 0;
		this.scrollLastPosition = 0;
		this.scrollPosition = 0;
	},

	// Build API parameters from selected state
	buildApiParams () {
		const getValues = element => element.value;

		const params = {
			from: this.selected.searchParamsFromIata.map(getValues).flat(),
			to: this.selected.searchParamsToIata.map(getValues).flat(),
			leave: this.selected.searchParamsLeaveDate.map(element => element.value),
			triplength: this.selected.searchParamsTripLength.map(element => element.value),
			content: this.selected.content,
			adults: this.selected.searchParamsAdults,
			sort: this.selected.sort
		};

		// Add page if greater than 0 (for pagination)
		if (this.selected.page > 0) {
			params.page = this.selected.page;
		}

		// Delete empty values
		Object.keys(params).forEach((key) => {
			if (Array.isArray(params[key]) && params[key].length === 0) {
				delete params[key];
			}
		});

		return params;
	},

	// Build URL with current parameters
	buildUrl (params) {
		let url = window.location.origin + '/' + lLink('global.navigation.lastMinuteLink');
		url = url.split(/[?#]/)[0];

		Object.entries(params).forEach(([key, value]) => {
			if (key !== 'page') { // Don't add page to URL
				const urlParams = Array.isArray(value) ? value.join(',') : value;
				url = setUrlParameter(url, key, urlParams);
			}
		});

		return url;
	},

	// Connect to the API with selected params and request results
	getResults (params) {
		if (!isVisible(this.elems.ListLoader)) {
			fadeIn(this.elems.ListLoader, 400);
		}

		if (this.request) {
			this.request.abort?.();
			this.selected.page = Math.max(0, this.selected.page - 1);
		}

		this.request = ConnectApi('Lastminute', 'getData', [{ params }])
			.then((response) => {
				// Clear container if it's the first page
				if (!this.elems.ListContainer.hasChildNodes() || this.selected.page === 0) {
					this.elems.ListContainer.innerHTML = response.rowsHtml;
				} else {
					this.elems.ListContainer.insertAdjacentHTML('beforeend', response.rowsHtml);
				}

				// Update discount display
				if (response.discount && response.discount !== 0) {
					qs('.abTabNav--lastMinuteSortingColumns .abTabNav__btn.discount .abTabNav__deal').innerHTML = `-${response.discount}%`;
					qs('.abTabNav--lastMinuteSortingColumns .abTabNav__btn.discount').classList.remove('disabled');
				} else {
					qs('.abTabNav--lastMinuteSortingColumns .abTabNav__btn.discount').classList.add('disabled');
				}
				qs('.abTabNav--lastMinuteSortingColumns .abTabNav__btn.best .abTabNav__deal').innerHTML = response.cheapest;

				this.toIataProps.items = response.toIatas;
			})
			.finally(() => {
				fadeOut(this.elems.ListLoader, 180);
				this.request = false;
			});

		return this.request;
	}
};

documentReady(function () {
	window.lastminute = lastminute;
	lastminute.init();
});
